// Schedule generation algorithm
// Analyzes historical data and produces a draft schedule for a target year

import {
  getHolidaysForYears,
  adjustStartForHolidays,
  addTeachingDayOffset,
  countTeachingDays,
  getTeachingDatesInRange,
  getTeachingEndDate,
  isTeachingDay,
  isValidTeachingBlock
} from './holidays';
import { canonicalizeDepartment, inferDepartmentFromName } from './departmentColors';
import {
  isExcludedName,
  courseMatches,
  locationMatches,
  describeRule,
  validateSchedulingRules,
  RULE_TYPES,
  BUILT_IN_SCHEDULING_RULES
} from './schedulingRules';

export const analyzeHistoricalData = (sessions, options = {}) => {
  const { excludeKeywords = [] } = options;
  // Drop excluded courses (built-in IYT / Special / NOAA plus any manager-added
  // keywords) so they never become course profiles or get scheduled.
  const includedSessions = (sessions || []).filter(s => !isExcludedName(s.name, excludeKeywords));
  const historicalYears = getUniqueYears(includedSessions);
  const historicalHolidayMap = getHolidaysForYears([
    ...historicalYears,
    ...historicalYears.map(year => year + 1)
  ]);
  // Include cancelled sessions in the analysis so courses that were planned
  // but never ran (e.g. Leadership / Managerial) still get scheduled into
  // future drafts. Cancelled rows are tracked separately so we can flag the
  // resulting drafts for management review.
  const usableSessions = includedSessions.filter(s => s.stateCode !== 1 || s.cancelled);
  // The original "active" (i.e. non-cancelled) set is still useful for things
  // like fill-rate calculations and instructor frequency.
  const activeSessions = usableSessions.filter(s => !s.cancelled);

  // Build session-by-id map for resolving bundle children
  const sessionsById = {};
  activeSessions.forEach(s => { if (s.id) sessionsById[s.id] = s; });

  // Date threshold used to weight "recent" departments more heavily, since
  // departments only became consistently populated after the calendar utility
  // launched. Anything within the last 18 months is considered "recent".
  const recentCutoff = new Date();
  recentCutoff.setMonth(recentCutoff.getMonth() - 18);

  // Group by course name (normalized) — exclude bundle children at the top level
  // (they'll appear nested under their parent bundle course)
  const courseMap = {};
  usableSessions.forEach(session => {
    if (session.isBundleChild) return; // Children handled via parent's bundle profile
    // Group by the real course identity (mi_course lookup) so every session of
    // the same course collapses into one profile. This makes the course count
    // reflect actual courses (~150) rather than the thousands of session-name
    // variants. Sessions with no course lookup fall back to grouping by
    // normalized session name so they still appear.
    const courseKey = session.courseGuid || `name:${normalizeName(session.name)}`;
    if (!courseMap[courseKey]) {
      courseMap[courseKey] = {
        name: session.courseName || session.name, // Prefer the real course name (mi_course)
        sessions: [],
        locations: new Set(),
        locationNames: [], // ordered, with repeats — used to derive most-common
        departments: [],
        recentDepartments: [],   // departments observed in the last 18 months
        classrooms: [],
        campuses: [],
        instructors: [],
        cancelledCount: 0,       // running count of cancelled historical sessions
        totalCount: 0,           // running count of all historical sessions
        bundleChildPatterns: [] // [{ name, dayOffset, duration }]
      };
    }
    courseMap[courseKey].sessions.push(session);
    courseMap[courseKey].totalCount++;
    if (session.cancelled) courseMap[courseKey].cancelledCount++;
    if (session.locationGuid) courseMap[courseKey].locations.add(session.locationGuid);
    if (session.locationName) courseMap[courseKey].locationNames.push(session.locationName);
    if (session.departmentName) {
      courseMap[courseKey].departments.push(session.departmentName);
      // Recent departments get prioritised when picking the suggested department
      const sessionDate = session.startDate ? new Date(session.startDate) : null;
      if (sessionDate && !isNaN(sessionDate) && sessionDate >= recentCutoff) {
        courseMap[courseKey].recentDepartments.push(session.departmentName);
      }
    }
    if (session.classroomName) courseMap[courseKey].classrooms.push(session.classroomName);
    if (session.campusName) courseMap[courseKey].campuses.push(session.campusName);
    // Collect instructor names from any available shape:
    //   session.instructor (primary, populated via mi_instructors enrichment)
    //   session.instructors[] (full list of co-instructors)
    //   session.mi_instructor (legacy raw text field, if present)
    const instructorCandidates = [];
    if (session.instructor) instructorCandidates.push(session.instructor);
    if (Array.isArray(session.instructors)) instructorCandidates.push(...session.instructors);
    if (typeof session.mi_instructor === 'string') instructorCandidates.push(session.mi_instructor);
    instructorCandidates
      .map(v => (typeof v === 'string' ? v.trim() : ''))
      .filter(v => v && v !== 'TBD')
      .forEach(v => courseMap[courseKey].instructors.push(v));

    // Capture bundle child pattern for this course
    if (session.isBundleParent && session.childSessionIds && session.childSessionIds.length > 0) {
      const parentStart = parseLocalDate(session.startDate);
      session.childSessionIds.forEach(childId => {
        const child = sessionsById[childId];
        if (!child || !child.startDate || !parentStart) return;
        const childStart = parseLocalDate(child.startDate);
        const childEnd = parseLocalDate(child.endDate || child.startDate);
        if (!childStart || !childEnd) return;
        const dayOffset = countTeachingDayOffset(parentStart, childStart, historicalHolidayMap);
        const duration = Math.max(1, countTeachingDays(childStart, childEnd, historicalHolidayMap));
        courseMap[courseKey].bundleChildPatterns.push({
          courseGuid: child.courseGuid || null,
          name: child.courseName || stripDatedSessionSuffix(child.name) || 'Untitled',
          dayOffset,
          duration,
          maxCapacity: child.maxCapacity || 0,
          locationGuid: child.locationGuid || null,
          department: child.departmentName || '',
          classroom: child.classroomName || '',
          campus: child.campusName || ''
        });
      });
    }
  });

  // ────────────────────────────────────────────────────────────────────
  // PA-known department set: the only valid targets for auto-assignment.
  // Anything not observed in Power Apps history is excluded so we don't
  // assign a department PA can't accept (e.g. removed/renamed values).
  // ────────────────────────────────────────────────────────────────────
  const paDepartmentCounts = {};
  usableSessions.forEach(s => {
    const canonical = canonicalizeDepartment(s.departmentName);
    if (!canonical) return;
    paDepartmentCounts[canonical] = (paDepartmentCounts[canonical] || 0) + 1;
  });
  const paDepartmentSet = new Set(Object.keys(paDepartmentCounts));
  // Most-common PA dept — used as a guaranteed-valid final fallback in place
  // of the hardcoded 'N/A' (which may not exist in PA's option set).
  const fallbackPaDepartment = Object.entries(paDepartmentCounts)
    .sort((a, b) => b[1] - a[1])[0]?.[0] || null;

  // ────────────────────────────────────────────────────────────────────
  // Learned department map: derive token → canonical-dept associations
  // from EVERY historical session that already has a canonical department.
  // Courses without their own historical dept can then be classified by
  // the same naming patterns the org has used historically (e.g. every
  // course whose name contains "welding" has historically been classified
  // as "Shipyard Boot Camp/Welding" → new welding courses follow suit).
  // Only PA-observed departments are eligible (see paDepartmentSet above).
  // ────────────────────────────────────────────────────────────────────
  const tokenDeptCounts = {};   // { token: { dept: count } }
  usableSessions.forEach(s => {
    const dept = canonicalizeDepartment(s.departmentName);
    if (!dept || !paDepartmentSet.has(dept)) return;
    extractTokens(s.name).forEach(tok => {
      if (!tokenDeptCounts[tok]) tokenDeptCounts[tok] = {};
      tokenDeptCounts[tok][dept] = (tokenDeptCounts[tok][dept] || 0) + 1;
    });
  });

  // Reduce to: token → { dept, support } picking the dominant dept per token,
  // requiring at least MIN_SUPPORT historical observations to avoid noise.
  const MIN_SUPPORT = 2;
  const learnedDepartmentMap = {};
  Object.entries(tokenDeptCounts).forEach(([tok, counts]) => {
    let bestDept = null;
    let bestCount = 0;
    let total = 0;
    Object.entries(counts).forEach(([dept, c]) => {
      total += c;
      if (c > bestCount) { bestDept = dept; bestCount = c; }
    });
    // Require dominance (≥60% of observations) AND minimum support
    if (bestDept && bestCount >= MIN_SUPPORT && bestCount / total >= 0.6) {
      learnedDepartmentMap[tok] = { dept: bestDept, support: bestCount };
    }
  });

  // Build course profiles
  const courseProfiles = Object.entries(courseMap).map(([key, data]) => {
    const { sessions: courseSessions } = data;
    // Use one calendar-year denominator for the whole portfolio. If a course
    // did not appear in an analyzed year, that is a real zero rather than a
    // reason to remove the year from its annual average.
    const yearCount = historicalYears.length || 1;
    // Only non-cancelled sessions inform enrollment/fill-rate metrics; using
    // cancelled rows here would drag fill-rates toward zero unfairly.
    const ranSessions = courseSessions.filter(s => !s.cancelled);

    // Monthly distribution (which months this course typically runs)
    const monthlyDistribution = getMonthlyDistribution(courseSessions);

    // Frequency: average times per year — counts cancelled too because we
    // still want to schedule a recurring class even if recent runs were cancelled.
    const yearFrequencies = {};
    courseSessions.forEach(s => {
      const year = new Date(s.startDate).getFullYear();
      yearFrequencies[year] = (yearFrequencies[year] || 0) + 1;
    });
    const avgFrequency = Math.round(
      Object.values(yearFrequencies).reduce((a, b) => a + b, 0) / yearCount
    );

    // Enrollment stats — use only sessions that actually ran
    const enrollments = ranSessions.map(s => s.actualEnrolled);
    const capacities = ranSessions.map(s => s.maxCapacity).filter(c => c > 0);
    const fillRates = ranSessions.map(s => s.fillRate).filter(r => r > 0);

    // Cancellation rate over historical sessions
    const cancelRate = data.totalCount > 0 ? (data.cancelledCount / data.totalCount) : 0;
    const historicallyCancelled = cancelRate >= 0.5; // ≥ 50% cancelled = needs review

    // Department resolution — prefer the most-common RECENT (post-launch)
    // department that matches the canonical color-coded list. Fall back to
    // any historical department; then to a name-based keyword inference;
    // finally to 'N/A' so every session gets a color-coded department.
    const recentCanonical = pickCanonicalDepartment(data.recentDepartments);
    const historicalCanonical = pickCanonicalDepartment(data.departments);
    const fromHistory = recentCanonical || historicalCanonical;
    // Tier 3: learned token map — match this course's name tokens against
    // the dominant departments used by historically similar course names.
    // (learnedDepartmentMap is already restricted to PA-observed departments.)
    const learnedDept = fromHistory ? null : inferDepartmentFromLearnedMap(data.name, learnedDepartmentMap);
    // Tier 4: keyword rules — only honored if the suggested dept is one PA
    // actually accepts (i.e. has been observed in PA history). Otherwise
    // discarded so we never invent a department PA would reject.
    let keywordDept = null;
    if (!fromHistory && !learnedDept) {
      const candidate = inferDepartmentFromName(data.name);
      if (candidate && paDepartmentSet.has(candidate)) keywordDept = candidate;
    }
    // Final fallback: use the most-common dept observed in PA history rather
    // than the hardcoded 'N/A' (which may not exist in PA's option set).
    const suggestedDepartment = fromHistory || learnedDept || keywordDept || fallbackPaDepartment;
    // Track provenance so the UI can flag auto-assigned values for review
    // without blocking publication.
    const departmentSource = recentCanonical
      ? 'history-recent'
      : historicalCanonical
        ? 'history'
        : learnedDept
          ? 'inferred-from-history-tokens'
          : keywordDept
            ? 'inferred-from-keyword'
            : 'fallback-most-common-pa';

    // Typical session duration in instructional days. Historical date ranges
    // often cross weekends, which are not days the class is actually taught.
    const durations = courseSessions.map(s => {
      const start = parseLocalDate(s.startDate);
      const end = parseLocalDate(s.endDate);
      return Math.max(1, countTeachingDays(start, end, historicalHolidayMap));
    });

    // Historical day-of-week patterns (0=Sun ... 6=Sat)
    // Parse the ISO date prefix as a LOCAL date so we don't get a UTC-shifted
    // day-of-week (the same bug we fixed in the live calendar).
    const startDows = courseSessions
      .map(s => {
        const d = parseLocalDate(s.startDate);
        return d ? d.getDay() : null;
      })
      .filter(d => d !== null);
    const endDows = courseSessions
      .map(s => {
        const d = parseLocalDate(s.endDate);
        return d ? d.getDay() : null;
      })
      .filter(d => d !== null);
    const typicalStartDayOfWeek = mode(startDows);
    const typicalEndDayOfWeek = mode(endDows);
    // Secondary start DOW: useful when a course runs on two days a week
    // (e.g. Tue + Thu). Only kept if it has meaningful support (>= 25%).
    const secondaryStartDayOfWeek = secondMode(startDows, 0.25);

    // Week-of-month occurrence pattern: which week (1..5) does this course
    // typically start in? Captured as a ranked list so multi-times-per-month
    // courses can be placed on different weeks (e.g. weeks 2 and 4).
    const occurrenceCounts = {};
    courseSessions.forEach(s => {
      const d = parseLocalDate(s.startDate);
      if (!d) return;
      const occ = Math.floor((d.getDate() - 1) / 7) + 1; // 1..5
      occurrenceCounts[occ] = (occurrenceCounts[occ] || 0) + 1;
    });
    const occurrencePattern = Object.entries(occurrenceCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 2)
      .map(([occ]) => Number(occ));

    // Per-month average sessions (across all years) — drives true cadence
    // detection. A "twice a month" course will show ~2 in each preferred month.
    const perMonthCadence = monthlyDistribution.map(v => v / yearCount);

    // Per-location profiles: split this course's history by location so we
    // can stream placements per site (e.g. Hawaii gets its own 2x/month).
    const locationProfiles = buildLocationProfiles(courseSessions, yearCount);

    // Trend: is demand increasing or decreasing?
    const trend = calculateTrend(yearFrequencies);

    // Bundle child templates: collapse historical child patterns by child name,
    // taking the most common dayOffset/duration for each unique child course
    const bundleChildTemplates = summarizeBundleChildren(data.bundleChildPatterns);
    const isBundleParent = bundleChildTemplates.length > 0;

    return {
      key,
      name: data.name,
      totalSessions: courseSessions.length,
      avgFrequencyPerYear: avgFrequency,
      suggestedFrequency: getSuggestedFrequency(avgFrequency, trend),
      monthlyDistribution,
      avgEnrollment: average(enrollments),
      maxEnrollment: Math.max(...enrollments, 0),
      avgCapacity: average(capacities),
      typicalCapacity: mode(capacities) || average(capacities),
      avgFillRate: average(fillRates),
      typicalDuration: mode(durations) || Math.round(average(durations)),
      typicalStartDayOfWeek, // 0=Sun ... 6=Sat (null if no history)
      secondaryStartDayOfWeek, // 2nd most common start DOW (or null)
      typicalEndDayOfWeek,
      occurrencePattern,     // ranked week-of-month [2,4] etc
      perMonthCadence,       // [12] avg sessions/year per month
      locationProfiles,      // per-site stream metadata
      isBundleParent,
      bundleChildTemplates, // [{ name, dayOffset, duration, maxCapacity, ... }]
      trend, // 'increasing', 'stable', 'decreasing'
      yearFrequencies,
      locations: [...data.locations],
      // Most-common (mode) values from history for direct use in drafts
      suggestedLocationName: mostCommon(data.locationNames) || '',
      suggestedDepartment, // canonical-aware (recent → historical → name inference → N/A)
      departmentSource,    // 'history-recent' | 'history' | 'inferred-from-name'
      suggestedClassroom: mostCommon(data.classrooms) || '',
      suggestedCampus: mostCommon(data.campuses) || '',
      suggestedInstructor: mostCommon(data.instructors) || '',
      // Cancellation tracking — used to flag drafts for management review
      cancelRate,
      historicallyCancelled,
      cancelledCount: data.cancelledCount,
      totalCount: data.totalCount,
      // Full ranked lists for the UI (for filter dropdowns or multi-instructor display)
      departments: uniqueRanked(data.departments),
      classrooms: uniqueRanked(data.classrooms),
      campuses: uniqueRanked(data.campuses),
      instructorRanking: uniqueRanked(data.instructors),
      lastRun: courseSessions.reduce((latest, s) =>
        new Date(s.startDate) > new Date(latest) ? s.startDate : latest,
        courseSessions[0].startDate
      )
    };
  });

  // ────────────────────────────────────────────────────────────────────
  // Sequence detection: find course pairs that historically co-occur
  // (course A always followed by course B at the same location within
  // a few days). Annotates each follower with sequenceAfter metadata so
  // the scheduler can anchor it to its predecessor.
  // ────────────────────────────────────────────────────────────────────
  const sequenceMap = detectSequencePairs(usableSessions);
  courseProfiles.forEach(course => {
    const seq = sequenceMap[course.key];
    if (seq) course.sequenceAfter = seq;
  });

  // Sort by frequency (most common first)
  courseProfiles.sort((a, b) => b.totalSessions - a.totalSessions);

  return courseProfiles;
};

// Average the normalized annual portfolio. Profiles have already had excluded
// names and bundle children removed, making this the parent/standalone volume
// that a generated schedule should preserve.
export const calculateAnnualOfferingTarget = (courseProfiles) => {
  const profiles = courseProfiles || [];
  const years = [...new Set(profiles.flatMap(profile =>
    Object.keys(profile.yearFrequencies || {}).map(Number)
  ))];
  if (years.length === 0) return 0;
  const annualTotals = years.map(year => profiles.reduce(
    (sum, profile) => sum + Number(profile.yearFrequencies?.[year] || 0),
    0
  ));
  return Math.round(annualTotals.reduce((sum, total) => sum + total, 0) / years.length);
};

// Manager exclusions alter the course mix, not the required annual scale.
// Reassign excluded volume among enabled, historically productive courses.
// Hard ordered-group members remain unchanged so their one-to-one counts hold.
export const applyAnnualVolumeTarget = (courseProfiles, target, rules = [], targetYear = null) => {
  const blackoutRules = (rules || []).filter(rule =>
    rule.enabled !== false &&
    rule.type === RULE_TYPES.LOCATION_BLACKOUT &&
    (!rule.targetYear || Number(rule.targetYear) === Number(targetYear))
  );
  const profiles = (courseProfiles || [])
    .filter(profile => {
      const profileLocations = (profile.locationProfiles || [])
        .map(location => location.locationName)
        .filter(Boolean);
      if (profileLocations.length === 0) return true;
      return profileLocations.some(location =>
        !blackoutRules.some(rule => locationMatches(location, rule.location))
      );
    })
    .map(profile => ({ ...profile }));
  const desired = Math.max(0, Math.round(Number(target) || 0));
  let remaining = desired - profiles.reduce(
    (sum, profile) => sum + Math.max(1, Number(profile.suggestedFrequency) || 1),
    0
  );
  if (remaining <= 0 || profiles.length === 0) return profiles;

  const orderedTerms = (rules || [])
    .filter(rule => rule.enabled !== false && rule.severity !== 'soft' && rule.type === RULE_TYPES.ORDERED_GROUP)
    .flatMap(rule => rule.courses || []);
  const candidates = profiles
    .filter(profile => !orderedTerms.some(term => courseMatches(profile.name, term)))
    .map(profile => ({
      profile,
      added: 0,
      weight: Math.max(1, Number(profile.suggestedFrequency) || 1) *
        Math.max(0.25, Number(profile.avgFillRate) || 0.5)
    }));
  if (candidates.length === 0) return profiles;

  while (remaining > 0) {
    candidates.sort((a, b) =>
      (b.weight / (b.added + 1)) - (a.weight / (a.added + 1)) ||
      (Number(b.profile.avgEnrollment) || 0) - (Number(a.profile.avgEnrollment) || 0) ||
      String(a.profile.name || '').localeCompare(String(b.profile.name || ''))
    );
    candidates[0].profile.suggestedFrequency =
      Math.max(1, Number(candidates[0].profile.suggestedFrequency) || 1) + 1;
    candidates[0].added++;
    remaining--;
  }
  return profiles;
};

// Generate a draft schedule for a target year
// Spreading caps:
//  - MAX_PER_LOCATION_PER_DAY: one course per resolved location per day (a single
//    site can only physically run one new class on the same day).
//  - MAX_STARTS_PER_DAY: total parent starts per day across the institute.
const MAX_PER_LOCATION_PER_DAY = 1;
const MAX_STARTS_PER_DAY = 6;
export const PRIMARY_TRAINING_LOCATIONS = [
  'San Diego, CA',
  'Everett, WA',
  'Norfolk, VA'
];
const primaryLocationIndex = (name) => PRIMARY_TRAINING_LOCATIONS.findIndex(location =>
  String(location).toLowerCase() === String(name || '').trim().toLowerCase()
);
const isPrimaryTrainingLocation = (name) => primaryLocationIndex(name) >= 0;

// Try to figure out which location a course belongs to by scanning its name
// for any known location word (e.g. "BST – Hawaii", "OUPV (Honolulu)").
// Returns a {id, name} pair, or null if no match is found.
const resolveLocationFromName = (course, knownLocations) => {
  if (!course?.name || !Array.isArray(knownLocations) || knownLocations.length === 0) return null;
  const haystack = course.name.toLowerCase();
  // Prefer the longest matching location name to avoid 'OCC' matching 'Occupational'
  const matches = knownLocations
    .filter(l => l?.name && haystack.includes(String(l.name).toLowerCase()))
    .sort((a, b) => b.name.length - a.name.length);
  if (matches.length === 0) return null;
  return { id: matches[0].id || null, name: matches[0].name };
};

export const generateDraftSchedule = (courseProfiles, targetYear, locations = [], rules = []) => {
  const draft = [];
  // Manager-defined structured rules (enabled only). Enforced deterministically
  // during placement (scoring) and re-checked in a final validation pass.
  const activeRules = [...BUILT_IN_SCHEDULING_RULES, ...(rules || [])]
    .filter(r => r && r.enabled !== false);
  const ruleErrors = validateSchedulingRules(activeRules, {
    courseNames: (courseProfiles || []).map(course => course.name)
  });
  if (ruleErrors.length > 0) {
    throw new Error(`Scheduling rules must be corrected before generating:\n• ${ruleErrors.join('\n• ')}`);
  }
  const builtInConstraintErrors = [];
  activeRules.filter(rule => rule.severity !== 'soft').forEach(rule => {
    if (rule.type === RULE_TYPES.START_DAY_OF_WEEK) {
      const course = courseProfiles.find(profile => courseMatches(profile.name, rule.courseA));
      if (!course || (course.typicalDuration || 1) > 5) return;
      const latestStartDay = 6 - Math.max(1, course.typicalDuration || 1);
      const possibleDays = (rule.daysOfWeek || []).filter(day => day >= 1 && day <= latestStartDay);
      if (possibleDays.length === 0) {
        builtInConstraintErrors.push(
          `"${course.name}" is ${course.typicalDuration || 1} instructional days and cannot start on the selected weekday(s).`
        );
      }
    }
    if (rule.type === RULE_TYPES.ORDERED_GROUP) {
      const groupCourses = (rule.courses || []).map(term =>
        courseProfiles.find(profile => courseMatches(profile.name, term))
      );
      if (groupCourses.some(course => !course)) return;
      const minimumDays = groupCourses.reduce((sum, course) => sum + Math.max(1, course.typicalDuration || 1), 0);
      if (minimumDays > (rule.gapDays || 7)) {
        builtInConstraintErrors.push(
          `Ordered group needs at least ${minimumDays} instructional days, but its whole-group maximum is ${rule.gapDays || 7}.`
        );
      }
    }
  });
  if (builtInConstraintErrors.length > 0) {
    throw new Error(`Scheduling rules conflict with built-in teaching-day requirements:\n• ${builtInConstraintErrors.join('\n• ')}`);
  }
  const appliesToTargetYear = (rule) =>
    !rule.targetYear || Number(rule.targetYear) === Number(targetYear);
  const isBlackedOutLocation = (locationName) => activeRules.some(rule =>
    rule.type === RULE_TYPES.LOCATION_BLACKOUT &&
    locationMatches(locationName, rule.location) &&
    appliesToTargetYear(rule)
  );
  const sameLocation = (a, b) =>
    String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
  const reassignLocationStreams = (streams) => {
    const reassignments = activeRules.filter(rule =>
      rule.type === RULE_TYPES.LOCATION_REASSIGN &&
      rule.fromLocation && rule.toLocation &&
      appliesToTargetYear(rule)
    ).sort((a, b) => Number(!!b.targetYear) - Number(!!a.targetYear));
    if (reassignments.length === 0) return streams;

    const output = new Map();
    streams.forEach(stream => {
      let next = { ...stream };
      const seenLocations = new Set();
      for (let step = 0; step < reassignments.length; step++) {
        const currentName = next.locationName;
        const currentKey = String(currentName || '').trim().toLowerCase();
        if (seenLocations.has(currentKey)) break; // validation rejects cycles; safety only
        seenLocations.add(currentKey);
        const rule = reassignments.find(candidate =>
          sameLocation(currentName, candidate.fromLocation)
        );
        if (!rule) break;
        const catalogLocation = locations.find(location =>
          sameLocation(location.name, rule.toLocation)
        );
        const destinationProfile = streams.find(candidate =>
          sameLocation(candidate.locationName, rule.toLocation)
        );
        next = {
          ...next,
          locationGuid: catalogLocation?.id || destinationProfile?.locationGuid || null,
          locationName: catalogLocation?.name || destinationProfile?.locationName || rule.toLocation,
          wasReassigned: true,
          // Never carry a room assigned at the old campus into the replacement
          // location. Reuse an existing destination profile's defaults if known.
          suggestedClassroom: destinationProfile?.suggestedClassroom || '',
          suggestedCampus: destinationProfile?.suggestedCampus || next.suggestedCampus || ''
        };
      }

      const key = next.locationGuid || String(next.locationName || 'unassigned').toLowerCase();
      if (output.has(key)) {
        const existing = output.get(key);
        existing.count += next.count;
        if (!existing.suggestedClassroom && next.suggestedClassroom) existing.suggestedClassroom = next.suggestedClassroom;
        if (!existing.suggestedCampus && next.suggestedCampus) existing.suggestedCampus = next.suggestedCampus;
      } else {
        output.set(key, { ...next });
      }
    });
    return [...output.values()];
  };
  // Course-name terms that are "followers" in a SEQUENCE rule — these must be
  // placed AFTER their predecessor, so we defer them in the placement order.
  const sequenceRuleAnchors = activeRules
    .filter(r => r.type === RULE_TYPES.SEQUENCE && r.courseA && r.courseB)
    .map(r => ({
      term: r.courseB,
      location: r.location || '',
      after: { courseKey: normalizeName(r.courseA), gapDays: 1 }
    }));
  // Ordered groups: every course after the first must follow its predecessor.
  // Build an anchor lookup [{ term, after: { courseKey, gapDays } }] so those
  // followers can be deferred and anchored to the prior course's placement,
  // keeping the group together and in order.
  const orderedGroupAnchors = [];
  const ruleDependencies = activeRules
    .filter(r => r.type === RULE_TYPES.SEQUENCE && r.courseA && r.courseB)
    .map(r => ({ from: r.courseA, to: r.courseB, location: r.location || '' }));
  activeRules
    .filter(r => r.type === RULE_TYPES.ORDERED_GROUP && Array.isArray(r.courses) && r.courses.length >= 2)
    .forEach(r => {
      for (let i = 1; i < r.courses.length; i++) {
        orderedGroupAnchors.push({
          term: r.courses[i],
          location: r.location || '',
          after: { courseKey: normalizeName(r.courses[i - 1]), gapDays: r.gapDays || 1 }
        });
        ruleDependencies.push({
          from: r.courses[i - 1],
          to: r.courses[i],
          location: r.location || ''
        });
      }
    });
  const dependencyRank = (courseName, locationName, path = new Set()) => {
    const key = normalizeName(courseName);
    if (path.has(key)) return 0; // validation rejects cycles; safety only
    const predecessors = ruleDependencies.filter(dependency =>
      courseMatches(courseName, dependency.to) &&
      locationMatches(locationName, dependency.location)
    );
    if (predecessors.length === 0) return 0;
    const nextPath = new Set(path).add(key);
    return Math.max(...predecessors.map(dependency =>
      1 + dependencyRank(dependency.from, locationName, nextPath)
    ));
  };
  const occupiedSlots = {};         // `${locKey}|${date}` → count
  const globalStartCounts = {};     // total parent starts per date
  const occupiedClassroomDays = {}; // `${locKey}|${room}|${date}` → sessionId

  const holidayMap = getHolidaysForYears([targetYear - 1, targetYear, targetYear + 1]);

  // ────────────────────────────────────────────────────────────────────
  // Pass 1 — Build placement requests, one per (course × location × month-slot)
  // Per-location streams ensure "twice a month at all locations" produces
  // a separate stream per site rather than collapsing to one location.
  // ────────────────────────────────────────────────────────────────────
  const requests = [];
  courseProfiles.forEach(course => {
    // Resolve location streams: prefer historical per-location split.
    // Fallback for courses with no per-location history: a single stream
    // using the course's mode location (or name-derived one).
    let streams = course.locationProfiles && course.locationProfiles.length > 0
      ? allocateCountsByLocation(course.locationProfiles, course.suggestedFrequency)
      : null;

    if (!streams || streams.length === 0) {
      let guid = course.locations?.[0] || null;
      let name = course.suggestedLocationName || '';
      if ((!guid || !name) && locations.length > 0) {
        const fromName = resolveLocationFromName(course, locations);
        if (fromName) {
          guid = guid || fromName.id;
          name = name || fromName.name;
        }
      }
      streams = [{
        locationGuid: guid,
        locationName: name,
        suggestedClassroom: course.suggestedClassroom || '',
        suggestedCampus: course.suggestedCampus || '',
        count: course.suggestedFrequency
      }];
    }

    streams = reassignLocationStreams(streams);

    // A blackout closes a location, not the course's annual demand. Preserve
    // the course total across its remaining permitted historical locations.
    const availableStreams = streams.filter(stream => !isBlackedOutLocation(stream.locationName || ''));
    if (availableStreams.length === 0 && streams.some(stream => stream.count > 0)) {
      throw new Error(`Annual volume cannot be placed for "${course.name}": every historical location is blacked out for ${targetYear}.`);
    }
    if (availableStreams.length < streams.length) {
      streams = allocateCountsByLocation(
        availableStreams.map(stream => ({ ...stream, share: stream.count })),
        course.suggestedFrequency
      );
    }

    streams.forEach(stream => {
      if (!stream.count || stream.count <= 0) return;
      const monthSlots = buildMonthSlots(course.perMonthCadence, stream.count);
      // A request is a "follower" if the course is a learned sequence follower
      // OR it is the courseB of a manager SEQUENCE rule (must come after A).
      const sequenceAnchor = sequenceRuleAnchors.find(anchor =>
        courseMatches(course.name, anchor.term) &&
        locationMatches(stream.locationName || '', anchor.location)
      );
      const orderedAnchor = orderedGroupAnchors.find(anchor =>
        courseMatches(course.name, anchor.term) &&
        locationMatches(stream.locationName || '', anchor.location)
      );
      const isRuleFollower = !!sequenceAnchor || !!orderedAnchor;
      const ruleOrder = dependencyRank(course.name, stream.locationName || '');
      monthSlots.forEach((month, idx) => {
        requests.push({
          course,
          stream,
          month,
          idx,
          isFollower: !!course.sequenceAfter || isRuleFollower,
          sequenceAnchor,
          orderedAnchor,
          ruleOrder
        });
      });
    });
  });

  // Place non-followers first so followers can anchor to predecessor placements.
  requests.sort((a, b) => {
    if (a.isFollower !== b.isFollower) return a.isFollower ? 1 : -1;
    if (a.ruleOrder !== b.ruleOrder) return a.ruleOrder - b.ruleOrder;
    // Within each group, place high-frequency / low-month-flex courses first
    return b.course.suggestedFrequency - a.course.suggestedFrequency;
  });

  // ────────────────────────────────────────────────────────────────────
  // Pass 2 — Place each request using a scored candidate-date search.
  // ────────────────────────────────────────────────────────────────────
  requests.forEach(req => {
    const { course, stream, month, idx } = req;
    const locKey = stream.locationGuid || stream.locationName || 'unassigned';
    const room = (stream.suggestedClassroom || course.suggestedClassroom || '').trim();
    const campus = stream.suggestedCampus || course.suggestedCampus || '';

    // Sequence anchoring: if this course is a follower, find a recently-
    // placed predecessor at the same location and aim for predecessor end + gap.
    let anchorDate = null;
    if (req.isFollower) {
      // Explicit manager rules take precedence over a sequence learned from
      // history. Ordered groups use the matching predecessor occurrence.
      const after = req.orderedAnchor?.after || req.sequenceAnchor?.after || course.sequenceAfter;
      const pred = findRecentPredecessorPlacement(
        draft,
        after,
        locKey,
        req.orderedAnchor ? idx : null
      );
      if (pred && after) {
        const predEnd = parseLocalDate(pred.endDate);
        if (predEnd) {
          anchorDate = new Date(predEnd);
          anchorDate.setDate(anchorDate.getDate() + 1);
        }
      }
    }

    const placementOptions = {
      year: targetYear, month, course, locKey, room,
      occupiedSlots, globalStartCounts, occupiedClassroomDays,
      holidayMap, anchorDate, occurrenceIndex: idx,
      rules: activeRules, placedDraft: draft, locName: stream.locationName || '',
      enforcePrimaryWeekSeparation: isPrimaryTrainingLocation(stream.locationName)
    };
    let sessionDate = findBestSessionDate(placementOptions);
    let primarySeparationRelaxed = false;
    // Keep the same course out of the same calendar week at the three primary
    // sites. Relax only this built-in constraint when the target year has no
    // otherwise hard-compliant placement, and make that exception visible.
    if (!sessionDate && placementOptions.enforcePrimaryWeekSeparation) {
      sessionDate = findBestSessionDate({ ...placementOptions, enforcePrimaryWeekSeparation: false });
      primarySeparationRelaxed = !!sessionDate;
    }
    if (!sessionDate) {
      throw new Error(
        `No hard-rule-compliant date is available in ${targetYear} for "${course.name}"` +
        `${stream.locationName ? ` at ${stream.locationName}` : ''}. Adjust the course frequency or scheduling rules.`
      );
    }

    const endDate = getTeachingEndDate(sessionDate, course.typicalDuration, holidayMap);

    const parentId = `draft-${course.key}-${targetYear}-${locKey}-${month}-${idx}`;

    // Bundle children
    const childSessions = (course.bundleChildTemplates || []).map((tpl, ci) => {
      let childStart = addTeachingDayOffset(sessionDate, tpl.dayOffset, holidayMap);
      // A learned child offset may land too late in a week for its duration.
      // Advance to the next valid block while preserving child order.
      for (let attempt = 0; attempt < 15 && !isValidTeachingBlock(childStart, tpl.duration, holidayMap); attempt++) {
        childStart = addTeachingDayOffset(childStart, 1, holidayMap);
      }
      const childEnd = getTeachingEndDate(childStart, tpl.duration, holidayMap);
      const dept = canonicalizeDepartment(tpl.department) || course.suggestedDepartment || 'N/A';
      const cls = stream.wasReassigned ? room : (tpl.classroom || room);
      const cmp = stream.wasReassigned ? campus : (tpl.campus || campus);
      return {
        id: `${parentId}-child-${ci}`,
        parentDraftId: parentId,
        name: tpl.name,
        courseGuid: tpl.courseGuid || null,
        startDate: formatLocalIso(childStart),
        endDate: formatLocalIso(childEnd),
        maxCapacity: tpl.maxCapacity || Math.round(course.typicalCapacity),
        locationGuid: stream.wasReassigned ? stream.locationGuid : (tpl.locationGuid || stream.locationGuid || null),
        locationName: stream.locationName || '',
        department: dept,
        departmentName: dept,
        classroom: cls,
        classroomName: cls,
        campus: cmp,
        campusName: cmp,
        instructor: course.suggestedInstructor || 'TBD',
        mi_instructor: course.suggestedInstructor || 'TBD',
        isDraft: true,
        isBundleChild: true
      };
    });

    const reviewReasons = [];
    if (primarySeparationRelaxed) {
      reviewReasons.push(
        'Primary-location separation exception: no different hard-compliant week remained for this course at San Diego, Everett, or Norfolk'
      );
    }
    if (course.historicallyCancelled) {
      reviewReasons.push(
        `Historically cancelled (${Math.round((course.cancelRate || 0) * 100)}% of past runs) — confirm this should run`
      );
    }
    if (course.departmentSource === 'inferred-from-history-tokens') {
      reviewReasons.push(
        `Department auto-assigned to "${course.suggestedDepartment}" based on historical patterns of similarly-named courses — verify before publishing`
      );
    } else if (course.departmentSource === 'inferred-from-keyword') {
      reviewReasons.push(
        `Department auto-assigned to "${course.suggestedDepartment}" from course-name keywords (no history match) — verify before publishing`
      );
    } else if (course.departmentSource === 'fallback-most-common-pa') {
      reviewReasons.push(
        `Department defaulted to "${course.suggestedDepartment}" (most-common PA department) — no history match for this course; verify before publishing`
      );
    }

    const draftSession = {
      id: parentId,
      name: course.name,
      // Real course identity (mi_course GUID) when known — lets the revenue
      // report join to catalog prices reliably. 'name:'-prefixed keys are
      // name-based fallbacks (no course lookup), so store null there.
      courseGuid: (course.key && !String(course.key).startsWith('name:')) ? course.key : null,
      startDate: formatLocalIso(sessionDate),
      endDate: formatLocalIso(endDate),
      maxCapacity: Math.round(course.typicalCapacity),
      locationGuid: stream.locationGuid || null,
      locationName: stream.locationName || '',
      department: course.suggestedDepartment || 'N/A',
      departmentName: course.suggestedDepartment || 'N/A',
      departmentSource: course.departmentSource,
      classroom: room,
      classroomName: room,
      campus: campus,
      campusName: campus,
      instructor: course.suggestedInstructor || 'TBD',
      mi_instructor: course.suggestedInstructor || 'TBD',
      isBundleParent: childSessions.length > 0,
      childSessions,
      childSessionIds: childSessions.map(c => c.id),
      isDraft: true,
      confidence: calculateConfidence(course),
      basedOnSessions: course.totalSessions,
      scheduleOccurrenceIndex: idx,
      avgFillRate: course.avgFillRate,
      trend: course.trend,
      needsReview: reviewReasons.length > 0,
      reviewReasons,
      historicallyCancelled: !!course.historicallyCancelled,
      cancelRate: course.cancelRate || 0,
      suggestedBy: req.isFollower ? 'algorithm-sequenced' : 'algorithm',
      managerNotes: '',
      aiSuggestion: ''
    };

    // Reserve classroom occupancy for instructional dates only.
    reserveOccupancy({
      sessionDate, endDate, locKey, room,
      occupiedSlots, globalStartCounts, occupiedClassroomDays,
      sessionId: parentId, holidayMap
    });

    draft.push(draftSession);
    childSessions.forEach(child => draft.push(child));
  });

  // Sort by date
  draft.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

  // Final pass — flag classroom conflicts. If two or more sessions share the
  // same classroom on overlapping instructional dates, mark each for review
  // (the calendar UI will render a ⚠️ before the title).
  const classroomDayMap = {};   // `${classroom}|${dateKey}` → [sessionId, name]
  draft.forEach(s => {
    if (s.isBundleChild) return; // children inherit parent's review state
    const room = (s.classroom || s.classroomName || '').trim();
    if (!room) return;
    const start = parseLocalDate(s.startDate);
    const end = parseLocalDate(s.endDate);
    if (!start || !end) return;
    getTeachingDatesInRange(start, end, holidayMap).forEach(cur => {
      const k = `${room.toLowerCase()}|${formatLocalIso(cur).split('T')[0]}`;
      if (!classroomDayMap[k]) classroomDayMap[k] = [];
      classroomDayMap[k].push({ id: s.id, name: s.name, room });
    });
  });
  // Build a per-session conflict-other-name set
  const conflictsBySession = {};
  Object.values(classroomDayMap).forEach(group => {
    if (group.length < 2) return;
    group.forEach(g => {
      if (!conflictsBySession[g.id]) conflictsBySession[g.id] = new Set();
      group.forEach(other => {
        if (other.id !== g.id) conflictsBySession[g.id].add(`${other.name} (${g.room})`);
      });
    });
  });
  draft.forEach(s => {
    const conflicts = conflictsBySession[s.id];
    if (conflicts && conflicts.size > 0) {
      s.needsReview = true;
      const reason = `Classroom conflict with: ${[...conflicts].slice(0, 3).join('; ')}`;
      s.reviewReasons = [...(s.reviewReasons || []), reason];
      s.classroomConflict = true;
    }
  });

  // An ordered group means each course has a one-to-one partner at the scoped
  // location. Validate counts before per-session checks, which only establish
  // ordering and gap semantics for sessions that already exist.
  activeRules.filter(rule =>
    rule.type === RULE_TYPES.ORDERED_GROUP && rule.severity !== 'soft'
  ).forEach(rule => {
    const scopedParents = draft.filter(session =>
      !session.isBundleChild && locationMatches(session.locationName || '', rule.location)
    );
    const groupParents = scopedParents.filter(session =>
      (rule.courses || []).some(term => courseMatches(session.name, term))
    );
    if (groupParents.length === 0) return;
    const counts = (rule.courses || []).map(term =>
      groupParents.filter(session => courseMatches(session.name, term)).length
    );
    if (new Set(counts).size > 1 || counts.some(count => count === 0)) {
      throw new Error(`Hard ordered-group rule requires matching session counts: ${describeRule(rule)}.`);
    }
  });

  // Final pass — validate the complete draft. A hard residual means the rule
  // set cannot be satisfied as generated, so never return a misleading draft.
  // Soft residuals remain visible preferences for management review.
  if (activeRules.length > 0) {
    const hardViolationMessages = new Set();
    draft.forEach(s => {
      if (s.isBundleChild) return;
      const start = parseLocalDate(s.startDate);
      const end = parseLocalDate(s.endDate);
      if (!start || !end) return;
      const violations = evaluateAgainstRules(
        { name: s.name, start, end, locName: s.locationName || '', id: s.id, occurrenceIndex: s.scheduleOccurrenceIndex },
        activeRules, draft
      );
      if (violations.length === 0) return;
      violations.filter(v => v.severity === 'hard').forEach(v => hardViolationMessages.add(v.message));
      const unique = [...new Set(violations.map(v => v.message))];
      s.needsReview = true;
      s.ruleViolations = unique;
      s.reviewReasons = [
        ...(s.reviewReasons || []),
        ...unique.map(m => `Rule preference: ${m}`)
      ];
    });
    if (hardViolationMessages.size > 0) {
      throw new Error(`Generated draft violates hard scheduling rules:\n• ${[...hardViolationMessages].join('\n• ')}`);
    }
  }

  return draft;
};

// Detect scheduling conflicts in a draft — only flag when two or more sessions
// share the same CLASSROOM on the same day. Sessions without a classroom are
// not considered in conflict (a shared location alone is not a conflict).
export const detectConflicts = (draftSessions) => {
  const conflicts = [];
  const byDate = {};
  const years = new Set();
  draftSessions.forEach(session => {
    const start = parseLocalDate(session.startDate);
    const end = parseLocalDate(session.endDate);
    if (start) years.add(start.getFullYear());
    if (end) years.add(end.getFullYear());
  });
  const holidayMap = getHolidaysForYears([...years]);

  draftSessions.forEach(session => {
    const start = parseLocalDate(session.startDate);
    const end = parseLocalDate(session.endDate);
    if (!start || !end) return;

    getTeachingDatesInRange(start, end, holidayMap).forEach(current => {
      const dateKey = formatLocalIso(current).split('T')[0];
      if (!byDate[dateKey]) byDate[dateKey] = [];
      byDate[dateKey].push(session);
    });
  });

  Object.entries(byDate).forEach(([date, daySessions]) => {
    const classroomGroups = {};
    daySessions.forEach(s => {
      const room = (s.classroom || s.classroomName || '').trim();
      if (!room) return; // No classroom assigned — not a conflict
      if (!classroomGroups[room]) classroomGroups[room] = [];
      classroomGroups[room].push(s);
    });

    Object.entries(classroomGroups).forEach(([room, sessions]) => {
      if (sessions.length > 1) {
        conflicts.push({
          type: 'classroom',
          date,
          classroom: room,
          sessions: sessions.map(s => ({ id: s.id, name: s.name })),
          message: `${sessions.length} sessions share classroom "${room}" on ${date}`
        });
      }
    });
  });

  return conflicts;
};

// Generate summary statistics for the analysis dashboard
export const generateAnalyticsSummary = (courseProfiles, historicalSessions, options = {}) => {
  const { excludeKeywords = [] } = options;
  const activeSessions = historicalSessions
    .filter(s => !isExcludedName(s.name, excludeKeywords))
    .filter(s => !s.cancelled && s.stateCode !== 1);
  const years = getUniqueYears(activeSessions);

  // Overall fill rate distribution
  const fillRates = activeSessions.map(s => s.fillRate).filter(r => r > 0);
  const underUtilized = courseProfiles.filter(c => c.avgFillRate < 0.5);
  const wellUtilized = courseProfiles.filter(c => c.avgFillRate >= 0.5 && c.avgFillRate <= 0.9);
  const overSubscribed = courseProfiles.filter(c => c.avgFillRate > 0.9);

  // Seasonal patterns across all courses
  const overallMonthlyVolume = new Array(12).fill(0);
  activeSessions.forEach(s => {
    const month = new Date(s.startDate).getMonth();
    overallMonthlyVolume[month]++;
  });

  return {
    totalCourses: courseProfiles.length,
    totalHistoricalSessions: activeSessions.length,
    yearsAnalyzed: years.length,
    yearRange: years.length > 0
      ? `${Math.min(...years)} - ${Math.max(...years)}`
      : 'N/A',
    avgFillRate: average(fillRates),
    utilizationBreakdown: {
      underUtilized: underUtilized.length,
      wellUtilized: wellUtilized.length,
      overSubscribed: overSubscribed.length
    },
    monthlyVolume: overallMonthlyVolume,
    trendingUp: courseProfiles.filter(c => c.trend === 'increasing').length,
    trendingDown: courseProfiles.filter(c => c.trend === 'decreasing').length,
    stable: courseProfiles.filter(c => c.trend === 'stable').length
  };
};

// --- Helper functions ---

const normalizeName = (name) =>
  (name || '').trim().toLowerCase().replace(/\s+/g, ' ');

const stripDatedSessionSuffix = (name) => String(name || '')
  .replace(/\s+\d{1,2}[/-]\d{1,2}[/-]\d{2,4}(?:\s+.*)?$/i, '')
  .replace(/\s+\d{4}-\d{2}-\d{2}(?:\s+.*)?$/i, '')
  .trim();

const getUniqueYears = (sessions) => {
  const years = new Set();
  sessions.forEach(s => {
    if (s.startDate) years.add(new Date(s.startDate).getFullYear());
  });
  return [...years].sort();
};

const getMonthlyDistribution = (sessions) => {
  const dist = new Array(12).fill(0);
  sessions.forEach(s => {
    if (s.startDate) {
      dist[new Date(s.startDate).getMonth()]++;
    }
  });
  return dist;
};

// ──────────────────────────────────────────────────────────────────────
// Pattern-aware scheduling helpers (replaces old pickSessionDate /
// getPreferredMonths). See scheduleAlgorithm overview for design notes.
// ──────────────────────────────────────────────────────────────────────

// Build per-location course profiles so we can stream placements per site.
const buildLocationProfiles = (courseSessions, yearCount) => {
  const byLoc = {};
  courseSessions.forEach(s => {
    const guid = s.locationGuid || null;
    const name = s.locationName || '';
    const key = guid || name || 'unassigned';
    if (!byLoc[key]) byLoc[key] = {
      locationGuid: guid,
      locationName: name,
      sessions: [],
      classrooms: [],
      campuses: []
    };
    byLoc[key].sessions.push(s);
    if (s.classroomName) byLoc[key].classrooms.push(s.classroomName);
    if (s.campusName) byLoc[key].campuses.push(s.campusName);
  });
  return Object.values(byLoc).map(p => ({
    locationGuid: p.locationGuid,
    locationName: p.locationName,
    share: p.sessions.length / courseSessions.length,
    avgFrequencyPerYear: p.sessions.length / Math.max(1, yearCount),
    suggestedClassroom: mostCommon(p.classrooms) || '',
    suggestedCampus: mostCommon(p.campuses) || ''
  }));
};

// Largest-remainder allocation: split a total count across location streams
// proportionally to each stream's historical share. Guarantees the sum
// equals the requested total and each stream with positive share gets ≥1.
const allocateCountsByLocation = (locationProfiles, total) => {
  if (total <= 0 || locationProfiles.length === 0) return [];
  const totalShare = locationProfiles.reduce((s, p) => s + p.share, 0) || 1;
  const raw = locationProfiles.map(p => (p.share / totalShare) * total);
  const base = raw.map(Math.floor);
  let remaining = total - base.reduce((a, b) => a + b, 0);
  const remainders = raw
    .map((v, i) => ({ i, rem: v - base[i] }))
    .sort((a, b) => b.rem - a.rem);
  for (let i = 0; i < remaining && i < remainders.length; i++) base[remainders[i].i]++;
  return locationProfiles.map((p, i) => ({ ...p, count: base[i] })).filter(p => p.count > 0);
};

// Distribute N session-counts across 12 months proportionally to per-month
// cadence. A month can be picked multiple times (this is the fix for
// "twice-a-month" courses that used to flatten to once-per-month).
const buildMonthSlots = (perMonthCadence, totalCount) => {
  if (totalCount <= 0) return [];
  const weights = perMonthCadence && perMonthCadence.some(v => v > 0)
    ? perMonthCadence
    : new Array(12).fill(1);
  const totalWeight = weights.reduce((a, b) => a + b, 0) || 1;
  const raw = weights.map(w => (w / totalWeight) * totalCount);
  const base = raw.map(Math.floor);
  let remaining = totalCount - base.reduce((a, b) => a + b, 0);
  const remainders = raw
    .map((v, month) => ({ month, rem: v - base[month] }))
    .sort((a, b) => b.rem - a.rem);
  for (let i = 0; i < remaining && i < remainders.length; i++) base[remainders[i].month]++;
  return base.flatMap((count, month) => Array(count).fill(month));
};

// Generate all candidate start dates in a month for a course using its
// historical DOW + week-of-month patterns. Falls back to all weekdays
// if the course has no DOW history.
const generateCandidateDates = (year, month, course, candidateDays = null) => {
  const historicalDays = [];
  if (course.typicalStartDayOfWeek != null) historicalDays.push(course.typicalStartDayOfWeek);
  if (course.secondaryStartDayOfWeek != null && !historicalDays.includes(course.secondaryStartDayOfWeek)) {
    historicalDays.push(course.secondaryStartDayOfWeek);
  }
  const dows = candidateDays && candidateDays.length > 0
    ? [...new Set(candidateDays)]
    : (historicalDays.length > 0 ? historicalDays : [1, 2, 3, 4]);

  const occurrences = course.occurrencePattern && course.occurrencePattern.length > 0
    ? course.occurrencePattern.slice()
    : [1, 2, 3, 4, 5];

  // Build candidates: each (DOW, occurrence) pair → one date if it exists in this month
  const candidates = [];
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  dows.forEach(dow => {
    for (let day = 1; day <= daysInMonth; day++) {
      const d = new Date(year, month, day);
      if (d.getDay() !== dow) continue;
      const occ = Math.floor((day - 1) / 7) + 1;
      const isPreferred = historicalDays.includes(dow) && occurrences.includes(occ);
      candidates.push({ date: d, dow, occ, isPreferred });
    }
  });
  return candidates;
};

// ─────────────────────────────────────────────────────────────────────────
// Structured-rule evaluation (deterministic enforcement).
// ─────────────────────────────────────────────────────────────────────────

// Two date spans overlap if each starts on or before the other ends.
const spansOverlap = (aStart, aEnd, bStart, bEnd) => aStart <= bEnd && bStart <= aEnd;

// Whole-day gap between two spans (0 if they overlap).
const daysBetweenSpans = (aStart, aEnd, bStart, bEnd) => {
  if (spansOverlap(aStart, aEnd, bStart, bEnd)) return 0;
  const ms = aStart > bEnd ? (aStart - bEnd) : (bStart - aEnd);
  return Math.floor(ms / (24 * 60 * 60 * 1000));
};

// Monday-keyed set of every ISO week a span touches (for same-week checks).
const weekKeysForSpan = (start, end) => {
  const keys = new Set();
  const cur = new Date(start);
  while (cur <= end) {
    const monday = new Date(cur);
    const dow = (monday.getDay() + 6) % 7; // 0 = Monday
    monday.setDate(monday.getDate() - dow);
    keys.add(formatLocalIso(monday).split('T')[0]);
    cur.setDate(cur.getDate() + 1);
  }
  return keys;
};

const shareAnyWeek = (aStart, aEnd, bStart, bEnd) => {
  const a = weekKeysForSpan(aStart, aEnd);
  for (const k of weekKeysForSpan(bStart, bEnd)) if (a.has(k)) return true;
  return false;
};

// Other non-child placed sessions, parsed into { name, locName, start, end }.
const placedSpans = (placedDraft, selfId) =>
  (placedDraft || [])
    .filter(s => !s.isBundleChild && s.id !== selfId)
    .map(s => ({
      name: s.name,
      locName: s.locationName || '',
      start: parseLocalDate(s.startDate),
      end: parseLocalDate(s.endDate),
      occurrenceIndex: s.scheduleOccurrenceIndex
    }))
    .filter(s => s.start && s.end);

// Evaluate one candidate/placed session against all rules. Returns an array of
// { severity, message } for every violation. Used both while scoring candidate
// dates and in the final validation pass over the finished draft.
// Exported for unit testing of rule semantics.
export const evaluateAgainstRules = (session, rules, placedDraft) => {
  const violations = [];
  if (!rules || rules.length === 0) return violations;
  const { name, start, end, locName, id, occurrenceIndex } = session;
  if (!start || !end) return violations;
  // Parsing every placed date is expensive for a full annual draft. Most rules
  // do not apply to most courses (and location rules need no placed sessions at
  // all), so build the comparison list only when an applicable relational rule
  // actually needs it.
  let others;
  const getOthers = () => {
    if (!others) others = placedSpans(placedDraft, id);
    return others;
  };
  const sev = (r) => (r.severity === 'soft' ? 'soft' : 'hard');

  rules.forEach(rule => {
    switch (rule.type) {
      case RULE_TYPES.MIN_GAP: {
        if (!courseMatches(name, rule.courseA) || !locationMatches(locName, rule.location)) break;
        const gapDays = rule.gapDays || 14;
        getOthers()
          .filter(o => courseMatches(o.name, rule.courseA) && locationMatches(o.locName, rule.location))
          .forEach(o => {
            if (daysBetweenSpans(start, end, o.start, o.end) < gapDays) {
              violations.push({ severity: sev(rule), message: describeRule(rule) });
            }
          });
        break;
      }
      case RULE_TYPES.NO_OVERLAP_SAME: {
        if (!courseMatches(name, rule.courseA) || !locationMatches(locName, rule.location)) break;
        getOthers()
          .filter(o => courseMatches(o.name, rule.courseA) && locationMatches(o.locName, rule.location))
          .forEach(o => {
            if (spansOverlap(start, end, o.start, o.end)) {
              violations.push({ severity: sev(rule), message: describeRule(rule) });
            }
          });
        break;
      }
      case RULE_TYPES.START_DAY_OF_WEEK: {
        if (!courseMatches(name, rule.courseA) || !locationMatches(locName, rule.location)) break;
        if (!(rule.daysOfWeek || []).includes(start.getDay())) {
          violations.push({ severity: sev(rule), message: describeRule(rule) });
        }
        break;
      }
      case RULE_TYPES.NO_OVERLAP_GROUP: {
        const terms = rule.courses || [];
        if (!terms.some(t => courseMatches(name, t)) || !locationMatches(locName, rule.location)) break;
        getOthers()
          .filter(o => terms.some(t => courseMatches(o.name, t)) && locationMatches(o.locName, rule.location))
          .forEach(o => {
            if (spansOverlap(start, end, o.start, o.end)) {
              violations.push({ severity: sev(rule), message: describeRule(rule) });
            }
          });
        break;
      }
      case RULE_TYPES.SAME_WEEK_BAN: {
        const isA = courseMatches(name, rule.courseA);
        const isB = courseMatches(name, rule.courseB);
        if ((!isA && !isB) || !locationMatches(locName, rule.location)) break;
        const otherTerm = isA ? rule.courseB : rule.courseA;
        getOthers()
          .filter(o => courseMatches(o.name, otherTerm) && locationMatches(o.locName, rule.location))
          .forEach(o => {
            if (shareAnyWeek(start, end, o.start, o.end)) {
              violations.push({ severity: sev(rule), message: describeRule(rule) });
            }
          });
        break;
      }
      case RULE_TYPES.SEQUENCE: {
        // Every generated courseB requires a courseA predecessor at the same
        // scoped location. A missing predecessor is a violation, not a no-op.
        if (!courseMatches(name, rule.courseB) || !locationMatches(locName, rule.location)) break;
        const preds = getOthers().filter(o => courseMatches(o.name, rule.courseA) && locationMatches(o.locName, rule.location));
        if (preds.length === 0) {
          violations.push({ severity: sev(rule), message: describeRule(rule) });
          break;
        }
        const hasPreceding = preds.some(o => o.end < start);
        if (!hasPreceding) {
          violations.push({ severity: sev(rule), message: describeRule(rule) });
        }
        break;
      }
      case RULE_TYPES.ORDERED_GROUP: {
        // Courses run in the listed order and the entire occurrence must fit
        // inside `gapDays` instructional days, from the first course's start
        // through the current/final course's end.
        const terms = rule.courses || [];
        if (terms.length < 2) break;
        const idx = terms.findIndex(t => courseMatches(name, t));
        if (idx === -1 || !locationMatches(locName, rule.location)) break;
        const maxGroupDays = rule.gapDays || 7;
        const matchesOccurrence = o =>
          occurrenceIndex == null || o.occurrenceIndex == null || o.occurrenceIndex === occurrenceIndex;

        // Enforce order against the matching occurrence of the immediate predecessor.
        if (idx > 0) {
          const predTerm = terms[idx - 1];
          const preds = getOthers().filter(o =>
            courseMatches(o.name, predTerm) &&
            locationMatches(o.locName, rule.location) &&
            matchesOccurrence(o)
          );
          const before = preds.filter(o => o.end < start);
          if (before.length === 0) {
            // Predecessor not scheduled before this course at all -> out of order.
            violations.push({ severity: sev(rule), message: describeRule(rule) });
          } else {
            const firstTerm = terms[0];
            const firstSessions = getOthers().filter(o =>
              courseMatches(o.name, firstTerm) &&
              locationMatches(o.locName, rule.location) &&
              matchesOccurrence(o) &&
              o.start < start
            );
            if (firstSessions.length === 0) {
              violations.push({ severity: sev(rule), message: describeRule(rule) });
            } else {
              const first = firstSessions.reduce((latest, candidate) =>
                candidate.start > latest.start ? candidate : latest
              );
              const holidayMap = getHolidaysForYears([
                first.start.getFullYear(),
                end.getFullYear()
              ]);
              if (countTeachingDays(first.start, end, holidayMap) > maxGroupDays) {
                violations.push({ severity: sev(rule), message: describeRule(rule) });
              }
            }
          }
        }

        break;
      }
      case RULE_TYPES.AVOID_CROSS_LOCATION: {
        if (!courseMatches(name, rule.courseA)) break;
        getOthers()
          .filter(o => courseMatches(o.name, rule.courseA) &&
            (o.locName || '').toLowerCase() !== (locName || '').toLowerCase())
          .forEach(o => {
            if (spansOverlap(start, end, o.start, o.end)) {
              violations.push({ severity: sev(rule), message: describeRule(rule) });
            }
          });
        break;
      }
      default:
        break;
    }
  });
  return violations;
};

// Score a candidate start date against current occupancy / preference signals.
// Lower score = better. Hard conflicts use big numbers so they're avoided
// unless every alternative is also conflicted.
const scoreCandidate = (candidate, ctx) => {
  const { course, locKey, room, occupiedSlots, globalStartCounts, occupiedClassroomDays, anchorDate, rules, placedDraft, locName, occurrenceIndex } = ctx;
  const startDate = candidate.date;
  const duration = course.typicalDuration || 1;
  let score = 0;

  // Check each instructional day — weekends and holidays do not occupy rooms.
  if (room) {
    const endDate = getTeachingEndDate(startDate, duration, ctx.holidayMap);
    getTeachingDatesInRange(startDate, endDate, ctx.holidayMap).forEach(cur => {
      const k = `${locKey}|${room.toLowerCase()}|${formatLocalIso(cur).split('T')[0]}`;
      if (occupiedClassroomDays[k]) score += 1000;
    });
  }

  const startKey = formatLocalIso(startDate).split('T')[0];
  const locSlot = `${locKey}|${startKey}`;
  if ((occupiedSlots[locSlot] || 0) >= MAX_PER_LOCATION_PER_DAY) score += 200;
  if ((globalStartCounts[startKey] || 0) >= MAX_STARTS_PER_DAY) score += 100;

  // Prefer DOW + week-of-month combinations that match historical patterns
  if (!candidate.isPreferred) score += 25;

  // If anchored to a predecessor (sequence follower), prefer dates near the anchor
  if (anchorDate) {
    const diffDays = Math.abs((startDate - anchorDate) / (24 * 60 * 60 * 1000));
    score += Math.min(diffDays * 5, 75);
  }

  // Manager-defined structured rules. Hard violations use a big penalty so they
  // are avoided (and trigger month expansion); soft ones are a strong nudge.
  if (rules && rules.length > 0) {
    const candEnd = getTeachingEndDate(startDate, duration, ctx.holidayMap);
    const violations = evaluateAgainstRules(
      { name: course.name, start: startDate, end: candEnd, locName, id: null, occurrenceIndex },
      rules, placedDraft || []
    );
    violations.forEach(v => { score += v.severity === 'hard' ? 1500 : 120; });
  }

  return score;
};

// Find the best hard-valid start date. Search the requested month first, then
// progressively widen across the target year. Historical weekdays remain a
// preference, but all weekdays are available when another hard rule requires
// moving farther than history alone would allow.
const findBestSessionDate = (opts) => {
  const { year, month, course, holidayMap, rules, locName } = opts;
  const hardWeekdayRules = (rules || []).filter(rule =>
    rule.enabled !== false &&
    rule.severity !== 'soft' &&
    rule.type === RULE_TYPES.START_DAY_OF_WEEK &&
    courseMatches(course.name, rule.courseA) &&
    locationMatches(locName, rule.location) &&
    Array.isArray(rule.daysOfWeek) && rule.daysOfWeek.length > 0
  );
  const allowedDays = hardWeekdayRules.length > 0
    ? hardWeekdayRules.slice(1).reduce(
      (days, rule) => days.filter(day => rule.daysOfWeek.includes(day)),
      [...hardWeekdayRules[0].daysOfWeek]
    )
    : null;
  const softWeekdayDays = (rules || [])
    .filter(rule =>
      rule.enabled !== false &&
      rule.severity === 'soft' &&
      rule.type === RULE_TYPES.START_DAY_OF_WEEK &&
      courseMatches(course.name, rule.courseA) &&
      locationMatches(locName, rule.location)
    )
    .flatMap(rule => rule.daysOfWeek || []);
  const historicalDays = [course.typicalStartDayOfWeek, course.secondaryStartDayOfWeek]
    .filter(day => day != null);
  const candidateDays = allowedDays && allowedDays.length > 0
    ? allowedDays
    : [...new Set([...historicalDays, ...softWeekdayDays, 1, 2, 3, 4, 5])];

  const adjustCandidate = (date) => {
    let adjusted = adjustStartForHolidays(new Date(date), course.typicalDuration, holidayMap);
    if (!allowedDays || allowedDays.length === 0) return adjusted;
    // A holiday can move (for example) a Friday start to Monday. Advance to
    // the next allowed weekday and re-check holidays so the hard rule survives.
    for (let attempt = 0; attempt < 8; attempt++) {
      if (allowedDays.includes(adjusted.getDay())) return adjusted;
      do {
        adjusted.setDate(adjusted.getDate() + 1);
      } while (!allowedDays.includes(adjusted.getDay()));
      adjusted = adjustStartForHolidays(adjusted, course.typicalDuration, holidayMap);
    }
    return adjusted;
  };

  const isHardValidDate = (date) => {
    const duration = course.typicalDuration || 1;
    if (!date || date.getFullYear() !== year || !isValidTeachingBlock(date, duration, holidayMap)) return false;
    const end = getTeachingEndDate(date, duration, holidayMap);
    if (opts.enforcePrimaryWeekSeparation && isPrimaryTrainingLocation(locName)) {
      const sameCourseAtOtherPrimarySite = placedSpans(opts.placedDraft || [], null).some(other =>
        courseMatches(other.name, course.name) &&
        isPrimaryTrainingLocation(other.locName) &&
        String(other.locName || '').toLowerCase() !== String(locName || '').toLowerCase() &&
        shareAnyWeek(date, end, other.start, other.end)
      );
      if (sameCourseAtOtherPrimarySite) return false;
    }
    return evaluateAgainstRules(
      { name: course.name, start: date, end, locName, id: null, occurrenceIndex: opts.occurrenceIndex },
      rules || [], opts.placedDraft || []
    ).every(violation => violation.severity !== 'hard');
  };

  const tryMonth = (m) => {
    let candidates = generateCandidateDates(year, m, course, candidateDays);
    if (candidates.length === 0) return null;
    // Adjust each candidate for holidays, keep starts inside the target year,
    // and remove duplicates created when multiple dates shift to one day.
    const seen = new Set();
    candidates = candidates.map(candidate => ({ ...candidate, date: adjustCandidate(candidate.date) }))
      .filter(candidate => candidate.date.getFullYear() === year)
      .filter(candidate => {
        const key = formatLocalIso(candidate.date).split('T')[0];
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .filter(candidate => isHardValidDate(candidate.date));
    if (candidates.length === 0) return null;
    let bestCandidate = null;
    let bestScore = Infinity;
    candidates.forEach(candidate => {
      const score = scoreCandidate(candidate, opts);
      if (score < bestScore) {
        bestCandidate = candidate;
        bestScore = score;
      }
    });
    return bestCandidate ? { date: bestCandidate.date, score: bestScore } : null;
  };

  const monthOrder = [month];
  for (let distance = 1; distance < 12; distance++) {
    if (month + distance < 12) monthOrder.push(month + distance);
    if (month - distance >= 0) monthOrder.push(month - distance);
  }
  let fallback = null;
  let fallbackScore = Infinity;
  for (const candidateMonth of monthOrder) {
    const best = tryMonth(candidateMonth);
    if (!best) continue;
    const score = best.score;
    if (score < fallbackScore) {
      fallback = best.date;
      fallbackScore = score;
    }
    // Preserve the existing behavior of expanding beyond a month whose only
    // choices have a classroom-level hard conflict.
    if (score < 1000) return best.date;
  }
  return fallback || null;
};

// Reserve instructional days only so weekends and holidays remain unoccupied.
const reserveOccupancy = (opts) => {
  const { sessionDate, endDate, locKey, room, occupiedSlots, globalStartCounts, occupiedClassroomDays, sessionId, holidayMap } = opts;
  const startKey = formatLocalIso(sessionDate).split('T')[0];
  occupiedSlots[`${locKey}|${startKey}`] = (occupiedSlots[`${locKey}|${startKey}`] || 0) + 1;
  globalStartCounts[startKey] = (globalStartCounts[startKey] || 0) + 1;
  if (room) {
    getTeachingDatesInRange(sessionDate, endDate, holidayMap).forEach(cur => {
      const k = `${locKey}|${room.toLowerCase()}|${formatLocalIso(cur).split('T')[0]}`;
      occupiedClassroomDays[k] = sessionId;
    });
  }
};

// Detect course-pair sequences from history. Returns a map keyed by the
// FOLLOWER course's normalized name → { courseKey: predecessor, gapDays, confidence }.
const detectSequencePairs = (sessions) => {
  // Group sessions by location and sort chronologically
  const byLoc = {};
  sessions.filter(s => !s.isBundleChild).forEach(s => {
    const key = s.locationGuid || s.locationName || 'unassigned';
    if (!byLoc[key]) byLoc[key] = [];
    byLoc[key].push(s);
  });

  // For each location, count A→B transitions where B starts within 0–7 days
  // of A's end and they are different courses.
  const pairCounts = {}; // `${followerKey}|${predKey}` → { count, gaps[] }
  const followerSeen = {}; // followerKey → totalOccurrences (denominator)
  Object.values(byLoc).forEach(locSessions => {
    locSessions.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
    locSessions.forEach((b, i) => {
      const bKey = normalizeName(b.name);
      followerSeen[bKey] = (followerSeen[bKey] || 0) + 1;
      // Look back up to 3 prior sessions
      for (let j = Math.max(0, i - 3); j < i; j++) {
        const a = locSessions[j];
        const aKey = normalizeName(a.name);
        if (aKey === bKey) continue;
        const gap = (new Date(b.startDate) - new Date(a.endDate)) / (24 * 60 * 60 * 1000);
        if (gap < 0 || gap > 7) continue;
        const pairKey = `${bKey}|${aKey}`;
        if (!pairCounts[pairKey]) pairCounts[pairKey] = { count: 0, gaps: [], aKey, bKey };
        pairCounts[pairKey].count++;
        pairCounts[pairKey].gaps.push(Math.round(gap));
      }
    });
  });

  // Pick the strongest predecessor per follower (≥2 observations, ≥60% confidence)
  const strongestByFollower = {};
  Object.values(pairCounts).forEach(p => {
    const support = p.count;
    const denom = followerSeen[p.bKey] || 1;
    const confidence = support / denom;
    if (support < 2 || confidence < 0.6) return;
    const existing = strongestByFollower[p.bKey];
    if (!existing || support > existing.support) {
      strongestByFollower[p.bKey] = {
        courseKey: p.aKey,
        gapDays: mode(p.gaps) ?? 1,
        confidence: Number(confidence.toFixed(2)),
        support
      };
    }
  });
  return strongestByFollower;
};

// Find the most recently-placed predecessor session for a follower at the
// same location. Returns null if none placed yet.
const findRecentPredecessorPlacement = (placedDraft, sequenceAfter, locKey, occurrenceIndex = null) => {
  if (!sequenceAfter) return null;
  const matches = placedDraft.filter(s =>
    !s.isBundleChild &&
    normalizeName(s.name) === sequenceAfter.courseKey &&
    (s.locationGuid || s.locationName || 'unassigned') === locKey &&
    (occurrenceIndex == null || s.scheduleOccurrenceIndex === occurrenceIndex)
  );
  if (matches.length === 0) return null;
  // Most recent
  matches.sort((a, b) => new Date(b.endDate) - new Date(a.endDate));
  return matches[0];
};

// Second-most-frequent value in an array; only returns it if its support
// is ≥ minShare of total observations (default 25%).
const secondMode = (arr, minShare = 0.25) => {
  if (!arr || arr.length === 0) return null;
  const counts = {};
  arr.forEach(v => { if (v != null) counts[v] = (counts[v] || 0) + 1; });
  const ranked = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  if (ranked.length < 2) return null;
  const [, secondCount] = ranked[1];
  if (secondCount / arr.length < minShare) return null;
  return Number(ranked[1][0]);
};

const calculateTrend = (yearFrequencies) => {
  const years = Object.keys(yearFrequencies).map(Number).sort();
  if (years.length < 2) return 'stable';

  const recent = years.slice(-2);
  const recentAvg = recent.reduce((sum, y) => sum + yearFrequencies[y], 0) / recent.length;
  const older = years.slice(0, -2);

  if (older.length === 0) return 'stable';

  const olderAvg = older.reduce((sum, y) => sum + yearFrequencies[y], 0) / older.length;

  if (recentAvg > olderAvg * 1.15) return 'increasing';
  if (recentAvg < olderAvg * 0.85) return 'decreasing';
  return 'stable';
};

const getSuggestedFrequency = (avgFrequency, trend) => {
  if (trend === 'increasing') return Math.max(1, avgFrequency + 1);
  if (trend === 'decreasing') return Math.max(1, avgFrequency - 1);
  return Math.max(1, avgFrequency);
};

const calculateConfidence = (course) => {
  // Higher confidence with more historical data points
  let score = 0;
  if (course.totalSessions >= 10) score += 30;
  else if (course.totalSessions >= 5) score += 20;
  else score += 10;

  // Consistent scheduling pattern
  const monthlyVariation = standardDeviation(course.monthlyDistribution);
  if (monthlyVariation > 3) score += 10; // Strong pattern
  else score += 5;

  // Stable trend
  if (course.trend === 'stable') score += 20;
  else score += 10;

  // Good fill rate data
  if (course.avgFillRate > 0) score += 20;

  // Multiple years of data
  const years = Object.keys(course.yearFrequencies).length;
  if (years >= 3) score += 20;
  else if (years >= 2) score += 10;

  return Math.min(100, score);
};

const average = (arr) => {
  if (!arr || arr.length === 0) return 0;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
};

const mode = (arr) => {
  if (!arr || arr.length === 0) return null;
  const counts = {};
  arr.forEach(v => { counts[v] = (counts[v] || 0) + 1; });
  const maxCount = Math.max(...Object.values(counts));
  const modeVal = Object.entries(counts).find(([, c]) => c === maxCount);
  return modeVal ? Number(modeVal[0]) : null;
};

const standardDeviation = (arr) => {
  if (!arr || arr.length === 0) return 0;
  const avg = average(arr);
  const squareDiffs = arr.map(v => Math.pow(v - avg, 2));
  return Math.sqrt(average(squareDiffs));
};

// Parse the ISO date prefix as a LOCAL midnight Date — avoids UTC day-shift
// (matches Calendar.js toLocalDate)
const parseLocalDate = (input) => {
  if (!input) return null;
  if (input instanceof Date) {
    return new Date(input.getFullYear(), input.getMonth(), input.getDate());
  }
  const isoMatch = String(input).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return new Date(+isoMatch[1], +isoMatch[2] - 1, +isoMatch[3]);
  const dt = new Date(input);
  if (!Number.isNaN(dt.getTime())) {
    return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
  }
  return null;
};

// Format a local Date as `YYYY-MM-DDT00:00:00` so the date prefix survives a
// later toLocalDate() round-trip in any timezone (no UTC shift).
const formatLocalIso = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}T00:00:00`;
};

// Instructional-day offset used by bundle child templates. An offset of five
// means the same weekday in the following week, not the intervening weekend.
const countTeachingDayOffset = (startDate, endDate, holidayMap) => {
  if (startDate.getTime() === endDate.getTime()) return 0;
  const direction = endDate > startDate ? 1 : -1;
  const current = new Date(startDate);
  let offset = 0;
  while (current.getTime() !== endDate.getTime()) {
    current.setDate(current.getDate() + direction);
    if (isTeachingDay(current, holidayMap)) offset += direction;
  }
  return offset;
};

// Most-common string in an array (mode for non-numeric values)
// Pick the most common canonical department name from a list of free-text
// department strings. Each input is normalized via canonicalizeDepartment;
// non-canonical entries are ignored. Returns the canonical name, or null.
const pickCanonicalDepartment = (arr) => {
  if (!arr || arr.length === 0) return null;
  const counts = {};
  arr.forEach(raw => {
    const canonical = canonicalizeDepartment(raw);
    if (canonical) counts[canonical] = (counts[canonical] || 0) + 1;
  });
  const entries = Object.entries(counts);
  if (entries.length === 0) return null;
  entries.sort((a, b) => b[1] - a[1]);
  return entries[0][0];
};

// Tokenize a course name into meaningful, lowercase tokens for the learned
// department classifier. Drops short tokens, pure-numeric tokens, and a
// small set of stopwords that carry no signal about department.
const TOKEN_STOPWORDS = new Set([
  'the', 'and', 'for', 'with', 'from', 'into', 'over', 'under', 'course',
  'training', 'class', 'session', 'level', 'intro', 'basic', 'advanced',
  'fundamentals', 'introduction', 'part', 'one', 'two', 'three', 'day',
  'days', 'week', 'weeks', 'mi', 'tr', 'tri', 'hours'
]);
const extractTokens = (name) => {
  if (!name || typeof name !== 'string') return [];
  return name
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(t => t.length >= 3 && !TOKEN_STOPWORDS.has(t) && !/^\d+$/.test(t));
};

// Score the course name's tokens against the learned token→dept map and
// return the dept with the highest summed support. Returns null if no
// tokens have a learned match.
const inferDepartmentFromLearnedMap = (name, learnedMap) => {
  if (!learnedMap) return null;
  const tokens = extractTokens(name);
  if (tokens.length === 0) return null;
  const deptScores = {};
  tokens.forEach(tok => {
    const entry = learnedMap[tok];
    if (!entry) return;
    deptScores[entry.dept] = (deptScores[entry.dept] || 0) + entry.support;
  });
  const entries = Object.entries(deptScores);
  if (entries.length === 0) return null;
  entries.sort((a, b) => b[1] - a[1]);
  return entries[0][0];
};

const mostCommon = (arr) => {
  if (!arr || arr.length === 0) return null;
  const counts = {};
  arr.forEach(v => {
    if (v == null || v === '') return;
    counts[v] = (counts[v] || 0) + 1;
  });
  const entries = Object.entries(counts);
  if (entries.length === 0) return null;
  entries.sort((a, b) => b[1] - a[1]);
  return entries[0][0];
};

// De-duplicated values, ordered by frequency (most common first)
const uniqueRanked = (arr) => {
  if (!arr || arr.length === 0) return [];
  const counts = {};
  arr.forEach(v => {
    if (v == null || v === '') return;
    counts[v] = (counts[v] || 0) + 1;
  });
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .map(([k]) => k);
};

// Collapse an array of historical bundle-child observations into one template
// per unique child course (using mode of dayOffset/duration/maxCapacity)
const summarizeBundleChildren = (patterns) => {
  if (!patterns || patterns.length === 0) return [];
  const byName = {};
  patterns.forEach(p => {
    const key = p.courseGuid || normalizeName(p.name);
    if (!byName[key]) {
      byName[key] = { courseGuid: p.courseGuid || null, name: p.name, offsets: [], durations: [], capacities: [], samples: [] };
    }
    byName[key].offsets.push(p.dayOffset);
    byName[key].durations.push(p.duration);
    byName[key].capacities.push(p.maxCapacity);
    byName[key].samples.push(p);
  });
  return Object.values(byName).map(g => {
    const sample = g.samples[0];
    return {
      courseGuid: g.courseGuid,
      name: g.name,
      dayOffset: mode(g.offsets) ?? Math.round(average(g.offsets)),
      duration: mode(g.durations) || Math.round(average(g.durations)) || 1,
      maxCapacity: Math.round(mode(g.capacities) || average(g.capacities) || 0),
      locationGuid: sample.locationGuid,
      department: sample.department,
      classroom: sample.classroom,
      campus: sample.campus
    };
  });
};
